import React, { Component, useCallback, useMemo } from 'react';
import OriginTracing from '@atlassiansox/origin-tracing';
import { ShareDialogContainer, type Flag, type ShareDialogContainerProps } from '@atlaskit/share';
import type { OptionData } from '@atlaskit/user-picker';
import type { ShareServiceClient } from '@atlaskit/share/clients';
import { getTenantContext_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useFlagsService, withFlagService, type FlagService } from '@atlassian/jira-flags';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { LegacyShareClientConfig } from '../../common/types.tsx';
import { createAri } from '../../common/utils/ari.tsx';
import LegacyShareClient from '../../services/legacy-share-client/index.tsx';
import { loadUsers } from '../../services/load-users/index.tsx';
import { UserRole } from '../user-role/index.tsx';
import type { ShareDialogFormData } from '../types.tsx';
import { useIsExtendedShareDialogEnabled } from '../../common/hooks/use-is-extended-share-dialog-enabled.tsx';
import { useProjectAccessLevel } from '../../services/use-project-access-level/index.tsx';
import { CustomShareSubmitButton } from '../custom-share-submit/index.tsx';
import type { UserRoleType } from '../../services/use-user-role/types.tsx';
import { useUserRole } from '../../services/use-user-role/index.tsx';
import type { Props, UserData, ServerUsersAndGroups, AdditionalUserFieldsProps } from './types.tsx';

export type ButtonProps = Props & {
	flagService: FlagService;
	onTriggerButtonClick?: () => void;
	shouldAutoDismissFlag?: boolean;
	legacyShareClientConfig?: LegacyShareClientConfig;
	orgId?: string;
	isBrowseUsersEnabled?: boolean;
	shareFormHelperMessage?: string;
	shareFieldsFooter?: React.ReactNode;
	workspaceAri?: string;
	customHeader?: React.ReactNode;
	userRoles?: UserRoleType[];
	userRolesLoading?: boolean;
} & Partial<Intl> &
	AdditionalUserFieldsProps &
	Pick<ShareDialogContainerProps, 'isExtendedShareDialogEnabled' | 'onSubmit'>;

const USER_PICKER_LIMIT = 20;

const AdditionalUserFields = ({
	userRoles,
	userRolesLoading,
}: { userRoles?: UserRoleType[]; userRolesLoading?: boolean } & AdditionalUserFieldsProps) =>
	userRoles && <UserRole isLoading={userRolesLoading} roles={userRoles} />;

// eslint-disable-next-line jira/react/no-class-components
export class ShareButton extends Component<ButtonProps> {
	static defaultProps = {
		shouldAutoDismissFlag: true,
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onTriggerButtonClick: () => {},
	};

	constructor(props: ButtonProps) {
		super(props);
		this.cloudId = getTenantContext_DEPRECATED_DO_NOT_USE().cloudId;

		if (props.legacyShareClientConfig !== undefined) {
			this.legacyShareClient = new LegacyShareClient(props.legacyShareClientConfig);
		}
	}

	legacyShareClient: undefined | ShareServiceClient;

	getOriginTracingInstance = (): OriginTracing =>
		new OriginTracing({ product: this.props.productId });

	loadUsers = async (searchText = ''): Promise<OptionData[]> => {
		const searchTextEncoded = encodeURIComponent(searchText);
		const url = `/rest/api/3/groupuserpicker?maxResults=${USER_PICKER_LIMIT}&query=${searchTextEncoded}&showAvatar=true&avatarSize=medium&excludeConnectAddons=true`;
		const groupsAndUsers: ServerUsersAndGroups = await fetchJson(url);
		return groupsAndUsers.users.users
			.filter((user) => user.accountType === 'atlassian')
			.map((user) => {
				const userData: UserData = {
					type: 'user',
					id: user.accountId,
					name: user.displayName,
					avatarUrl: user.avatarUrl,
				};
				return userData;
			});
	};

	showFlags = (flags: Flag[]) => {
		const { flagService, shouldAutoDismissFlag } = this.props;
		if (flagService?.showFlag) {
			flags.forEach((flag: Flag) => {
				flagService.showFlag({
					type: flag.appearance,
					title: flag.title.defaultMessage,
					isAutoDismiss: shouldAutoDismissFlag,
					messageId: `share-button.ui.share-view.showFlag.${flag.title.id}`,
					messageType: 'transactional',
				});
			});
		}
	};

	cloudId = '';

	render() {
		const {
			objectAri,
			renderCustomTriggerButton,
			shareLink,
			shareTitle,
			shareContentType,
			shareFormTitle,
			subProductId,
			triggerButtonAppearance = 'subtle',
			triggerButtonStyle,
			onTriggerButtonClick,
			shareIntegrations,
			integrationMode = 'off',
			isAutoOpenDialog,
			onDialogClose,
			copyTooltipText,
			orgId,
			isBrowseUsersEnabled,
			shareFormHelperMessage,
			shareFieldsFooter,
			additionalTabs,
			builtInTabContentWidth,
			isOutgoingMailDisabled,
			customHeader,
			isExtendedShareDialogEnabled,
			onSubmit,
			userRoles,
			userRolesLoading,
		} = this.props;

		return (
			<ShareDialogContainer
				shareClient={this.legacyShareClient}
				triggerButtonAppearance={triggerButtonAppearance}
				triggerButtonStyle={triggerButtonStyle}
				cloudId={this.cloudId}
				orgId={orgId}
				loadUserOptions={this.legacyShareClient && this.loadUsers}
				// @ts-expect-error - TS2322 - Type '() => OriginTracing' is not assignable to type 'OriginTracingFactory'.
				originTracingFactory={this.getOriginTracingInstance}
				productId={subProductId}
				renderCustomTriggerButton={renderCustomTriggerButton}
				shareAri={objectAri || createAri(subProductId, this.cloudId)}
				shareFormHelperMessage={shareFormHelperMessage}
				shareLink={shareLink}
				shareTitle={shareTitle}
				shareContentType={shareContentType}
				shareFormTitle={shareFormTitle}
				showFlags={this.showFlags}
				enableSmartUserPicker={!this.legacyShareClient}
				product="jira"
				onTriggerButtonClick={onTriggerButtonClick}
				shareIntegrations={shareIntegrations}
				integrationMode={integrationMode}
				isAutoOpenDialog={isAutoOpenDialog}
				onDialogClose={onDialogClose}
				copyTooltipText={copyTooltipText}
				isBrowseUsersDisabled={isBrowseUsersEnabled === false}
				shareFieldsFooter={shareFieldsFooter}
				workspaceAri={this.props.workspaceAri}
				additionalTabs={additionalTabs}
				builtInTabContentWidth={builtInTabContentWidth}
				isSubmitShareDisabled={isOutgoingMailDisabled}
				customHeader={customHeader}
				additionalUserFields={
					isExtendedShareDialogEnabled && (
						<AdditionalUserFields userRoles={userRoles} userRolesLoading={userRolesLoading} />
					)
				}
				isExtendedShareDialogEnabled={isExtendedShareDialogEnabled}
				onSubmit={onSubmit}
				CustomSubmitButton={isExtendedShareDialogEnabled ? CustomShareSubmitButton : undefined}
			/>
		);
	}
}

const noop = () => null;

const ShareButtonFC = ({
	objectAri,
	triggerButtonAppearance = 'subtle',
	onTriggerButtonClick = noop,
	integrationMode = 'off',
	isBrowseUsersEnabled,
	isOutgoingMailDisabled,
	legacyShareClientConfig,
	shouldAutoDismissFlag = false,
	onSubmit,
	userRoles,
	userRolesLoading,
	...props
}: Omit<ButtonProps, 'flagService'>) => {
	const cloudId = useCloudId();
	const { showFlag } = useFlagsService();
	const { data: projectContext } = useProjectContext();
	const isExtendedShareDialogEnabled = useIsExtendedShareDialogEnabled();

	const legacyShareClient = useMemo(
		() => legacyShareClientConfig && new LegacyShareClient(legacyShareClientConfig),
		[legacyShareClientConfig],
	);

	const getOriginTracingInstance = useCallback((): OriginTracing => {
		return new OriginTracing({ product: 'jira' });
	}, []);

	const showFlags = useCallback(
		(flags: Flag[]) => {
			if (showFlag) {
				flags.forEach((flag: Flag) => {
					showFlag({
						type: flag.appearance,
						title: flag.title.defaultMessage,
						isAutoDismiss: shouldAutoDismissFlag,
						messageId: `share-button.ui.share-view.showFlag.${flag.title.id}`,
						messageType: 'transactional',
					});
				});
			}
		},
		[showFlag, shouldAutoDismissFlag],
	);

	const { update: updateProjectAccessLevel } = useProjectAccessLevel({
		projectId: projectContext?.projectId,
	});

	const { add: addToUserRole } = useUserRole(projectContext?.projectKey);

	const handleOnSubmit = useCallback(
		(data: ShareDialogFormData) => {
			if (isExtendedShareDialogEnabled) {
				if (data?.projectAccessLevel?.value) {
					updateProjectAccessLevel({ accessLevel: data?.projectAccessLevel.value });
				}

				if (data.users && data?.userRole?.value) {
					const roleId = data?.userRole.value;
					const userIds: string[] = data.users
						.filter((user: UserData) => user.type === 'user')
						.map((user: UserData) => user.id);

					const groupIds: string[] = data.users
						.filter((user: UserData) => user.type === 'group')
						.map((group: UserData) => group.id);

					addToUserRole(roleId, {
						users: userIds,
						groups: groupIds,
					});
				}
			}

			onSubmit && onSubmit(data);
		},
		[isExtendedShareDialogEnabled, onSubmit, updateProjectAccessLevel, addToUserRole],
	);

	return (
		<ShareDialogContainer
			shareClient={legacyShareClient}
			cloudId={cloudId}
			loadUserOptions={legacyShareClient && loadUsers}
			// @ts-expect-error - TS2322 - Type '() => OriginTracing' is not assignable to type 'OriginTracingFactory'.
			originTracingFactory={getOriginTracingInstance}
			shareAri={objectAri || createAri(props.subProductId, cloudId)}
			showFlags={showFlags}
			enableSmartUserPicker={!legacyShareClient}
			product="jira"
			isBrowseUsersDisabled={isBrowseUsersEnabled === false}
			isSubmitShareDisabled={isOutgoingMailDisabled}
			triggerButtonAppearance={triggerButtonAppearance}
			onTriggerButtonClick={onTriggerButtonClick}
			integrationMode={integrationMode}
			onSubmit={handleOnSubmit}
			additionalUserFields={
				isExtendedShareDialogEnabled && (
					<AdditionalUserFields userRoles={userRoles} userRolesLoading={userRolesLoading} />
				)
			}
			CustomSubmitButton={isExtendedShareDialogEnabled ? CustomShareSubmitButton : undefined}
			{...props}
		/>
	);
};

export default componentWithCondition(
	() => fg('jira-growth-share-button-refactor-fc'),
	ShareButtonFC,
	injectIntl(withFlagService(ShareButton)),
);
